/**
 * De quelle page du site on arrive, pour qu'un bouton « retour » sache s'il a
 * le droit de remonter l'historique.
 *
 * ⛔⛔ POURQUOI CE N'EST PAS UN SIMPLE `history.back()`. La page de
 * certification est faite pour être atteignable directement, par quelqu'un
 * d'extérieur qui vérifie un certificat depuis un lien. Chez lui,
 * `history.back()` ne ramène pas au site : il le sort du site, vers la page
 * d'où il venait, quand il y en a une. Le bouton doit donc savoir s'il existe
 * une page PRÉCÉDENTE DU SITE dans cet onglet, et retomber sur l'accueil
 * sinon.
 *
 * ⚠️ La valeur est lue AU CLIC, jamais seulement au montage. React exécute les
 * effets des enfants avant ceux des parents : quand le bouton de la page se
 * monte, le suivi posé dans le gabarit racine n'a pas encore enregistré la
 * page qu'on vient de quitter. Un bouton qui déciderait au montage remonterait
 * d'un cran de trop.
 *
 * ⚠️ `sessionStorage` et non `localStorage` : la portée est l'ONGLET. Un
 * nouvel onglet sur un lien de certificat doit repartir vide, c'est
 * exactement le cas qu'on veut distinguer. Et il survit à un rechargement,
 * où l'historique du navigateur survit aussi, donc les deux restent d'accord.
 *
 * ⚠️ Chaque accès est protégé : en navigation privée ou avec les données de
 * site bloquées, `sessionStorage` LÈVE au lieu de rendre vide.
 */

const CLE = "ng:chemin-precedent";

let precedent: string | null = null;
let courant: string | null = null;
const abonnes = new Set<() => void>();

function lireStockage(): string | null {
  try {
    return sessionStorage.getItem(CLE);
  } catch {
    return null;
  }
}

function ecrireStockage(valeur: string | null) {
  try {
    if (valeur === null) sessionStorage.removeItem(CLE);
    else sessionStorage.setItem(CLE, valeur);
  } catch {
    /* rien à faire : le bouton retombera sur l'accueil, ce qui est le repli voulu */
  }
}

/** À appeler à chaque changement de chemin, depuis le gabarit racine. */
export function noterNavigation(chemin: string) {
  if (courant === null) {
    /*
     * Première page de cet onglet. On reprend ce qu'un chargement précédent
     * avait laissé : après un rechargement, l'historique du navigateur porte
     * encore la page d'avant, donc remonter est légitime.
     */
    precedent = lireStockage();
    courant = chemin;
    /* ⚠️ Sauf si c'est cette page-là : remonter y reviendrait. */
    if (precedent === chemin) {
      precedent = null;
      ecrireStockage(null);
    }
  } else if (courant !== chemin) {
    precedent = courant;
    courant = chemin;
    ecrireStockage(precedent);
  } else {
    return;
  }
  for (const notifier of abonnes) notifier();
}

/** Le chemin du site d'où l'on vient, ou `null` si on est arrivé directement. */
export function cheminPrecedent(): string | null {
  return precedent;
}

export function sAbonnerAuChemin(notifier: () => void): () => void {
  abonnes.add(notifier);
  return () => abonnes.delete(notifier);
}

/*
 * ⚠️ Instantané côté serveur : toujours `null`. Le rendu du serveur ne peut
 * rien savoir de l'onglet, et le faire mentir donnerait une différence
 * d'hydratation. Le bouton part donc de son état de repli — un vrai lien vers
 * l'accueil — et se met à jour après l'hydratation.
 */
export function cheminPrecedentServeur(): null {
  return null;
}
